import FileView from "components/fileView";
import DynamicFormInput from "components/form/form";
import TabNav from "components/TabNav";
import { useToast } from "contexts/ToastContext";
import { useState } from "react";
import { updateLegalAdvisory } from "services/legalAdvisoryService";
import { updateLegislativeDrafting } from "services/legislativeService";
import ShowUI, { LEGISLATIVE_DRAFTING_UI_RULES } from "utils/authRules";
import { LoadingIcon } from "utils/constant";
import { LEGAL_ADVISORY_REQUEST_STATUS, LEGAL_RESEARCH_REGISTRAR_TABS, LEGAL_RESEARCH_REQUEST_STATUS, LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { formatDay } from "utils/helpers";
import LegislativeDraftDetail from "./fragments/leftPanel";
import LegislativeDraftRightPanel from "./fragments/rightPanel";
import { useTranslation } from "react-i18next";

export default function RegistrarLegislativeDraft({ request }: { request: any }) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("Research Finding");
    const [letterRefNum, setLetterRefNum] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useToast();
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLetterRefNum(e.target.value)
    }
    
    const [formState, setFormState] = useState({ ...request });

    const handleSave = async () => {
        setSubmitting(true);
        const data = new FormData();
        data.append(
            "status",
            LEGAL_ADVISORY_REQUEST_STATUS.letter_refnum_submitted.value
        );
        data.append(
            "forward_status",
            LEGAL_ADVISORY_REQUEST_STATUS.assigned_to_prosecutor.value
        );
        data.append("registry_number", letterRefNum);

        try {
            const response = await updateLegalAdvisory(formState.id, data);

            setFormState({ ...response });
            showToast(t("LegalResearchRespondedBackSuccess") || "Legal research responded back successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegalResearchRequest") || "Failed to update legal research request.", "error");
        } finally {
            setSubmitting(false);
            setLetterRefNum("");
        }
    };

    return (
        <div>
            <ShowUI
                ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_reference_number.value}
                currentStatus={formState.status}
                forwardStatus={formState.forward_status}
            >
                <div className="flex mb-4 gap-2 font-semibold text-2xl text-[#696969]">
                    <svg className="my-auto" xmlns="http://www.w3.org/2000/svg" width="24" height="20" viewBox="0 0 24 20" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M0.335938 10.815C3.32099 11.9763 6.47113 12.6575 9.66929 12.8334V15.1666H14.3359V12.8334C17.5264 12.6335 20.6706 11.9693 23.6693 10.8616V19.8334H0.335938V10.815ZM15.5026 0L16.6693 1.16665V3.5H23.6693V9.57835C20.0921 10.914 16.3107 11.6205 12.4926 11.6666H11.6526C7.78322 11.6248 3.95185 10.898 0.335938 9.52V3.5H7.33594V1.16665L8.50259 0H15.5026ZM14.3359 2.33335H9.66929V3.5H14.3359V2.33335Z" fill="#696969" />
                    </svg>
                    <h1>{t("ConductLegislativeRequest") || "Conduct Legislative Request"}</h1>
                </div>
                {activeTab === "Research Finding" &&
                    <div className="flex w-full gap-8">
                        {formState?.draft_law_file &&
                            <div className="bg-white flex-1 p-4 rounded-lg border border-gray-200 shadow-md">
                                <div className="pb-2 border-b border-gray-200 mb-4">
                                    <p>{t("DraftLawDetail") || "Draft Law Detail"}</p>
                                </div>
                                {formState?.letter_reference_number &&
                                    <p className="text-[#747171] font-normal text-xs mb-3">{t("LetterReferenceNumber") || "Letter Reference Number"}: {formState?.letter_reference_number}</p>
                                }
                                <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.created_at).rest}</p>
                                <p className="font-regular text-base text-[#606060]">{formState?.draft_summary}</p>
                                <div className="flex border border-gray-200 p-2 rounded-lg my-4">
                                    <FileView title={t("DraftInstruction") || "Draft Instruction"} file={formState?.draft_law_file} />
                                </div>
                            </div>}
                        <div className="flex flex-col flex-1 max-w-4xl bg-white rounded-lg shadow-md p-6 mb-4 h-fit">
                            <DynamicFormInput
                                inputs={[
                                    {
                                        value: letterRefNum,
                                        type: "text",
                                        label: t("LetterReferenceNumber") || "Letter Reference Number",
                                        name: "letter_reference_number",
                                        onChange: handleChange,
                                        placeholder: t("LetterReferenceNumberPlaceholder") || "Eg. 12345",
                                    },
                                ]} />
                            <button 
                                onClick={handleSave} 
                                disabled={submitting}
                                className="bg-[#212121] hover:bg-[#141414] mt-4 text-white font-bold py-3 px-8 rounded-md w-full disabled:opacity-70"
                            >
                                {submitting ? (
                                    <div className="flex items-center justify-center">
                                        {LoadingIcon} {t("Submitting") || "Submitting..."}
                                    </div>
                                ) : (
                                    t("Save") || "Save"
                                )}
                            </button>
                        </div>
                    </div>
                }
            </ShowUI>
        </div>
    );
}